import React, { useState, useEffect } from "react";
import { PartnerCourse } from "../types/course";
import {
  fetchCourses,
  fetchCourseMappings,
  searchCourses,
} from "../services/api";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import Search from "./Search";

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    borderRadius: 15,
    margin: "1px 0 1px 0",
    maxWidth: 1200,
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: "#5e78d5",
    color: "#ffffff",
    borderBottom: "2px solid #99aab5",
    textAlign: "center",
  },
  tableCell: {
    borderBottom: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "center",
    cursor: "pointer",
  },
  paginationBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
    width: "100%",
  },
  paginationButton: {
    minWidth: "100px",
    backgroundColor: "#5e78d5",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#5b6eae",
    },
  },
  footer: {
    textAlign: "center",
    marginTop: theme.spacing(4),
  },
}));

const CourseTable: React.FC = () => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUniversity, setSelectedUniversity] = useState<string | null>(
    null
  );
  const [courseMappings, setCourseMappings] = useState<PartnerCourse[]>([]);
  const [allCourses, setAllCourses] = useState<PartnerCourse[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [universityInput, setUniversityInput] = useState("");

  const itemsPerPage = 10;

  useEffect(() => {
    fetchAllCourses();
  }, []);

  const fetchAllCourses = async () => {
    const data = await fetchCourses();
    setAllCourses(data);
    setCourseMappings(data);
  };

  const handleSearch = async (terms: string[]) => {
    if (terms.length === 0) {
      fetchAllCourses();
    } else {
      const data = await searchCourses(terms);
      setCourseMappings(data);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = courseMappings.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(courseMappings.length / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleUniversityClick = async (university: string) => {
    setSelectedUniversity(university);
    const mappings = await fetchCourseMappings(university);
    setCourseMappings(mappings);
  };

  const handleBackClick = () => {
    setSelectedUniversity(null);
    setCourseMappings(allCourses);
    setCurrentPage(1);
  };

  const handleHeaderClick = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogSubmit = () => {
    handleUniversityClick(universityInput);
    setOpenDialog(false);
  };

  const filteredCourseMappings = selectedUniversity
    ? courseMappings.filter(
        (course) => course.partnerUniversity === selectedUniversity
      )
    : courseMappings;

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Search onSearch={handleSearch} />
      {selectedUniversity ? (
        <>
          <Button onClick={handleBackClick} variant="contained" color="primary">
            Back to All Courses
          </Button>
          <Typography variant="h6" mt={2}>
            Course Mappings for {selectedUniversity}
          </Typography>
          <Typography variant="caption">
            Clear selected courses above to view all courses offered by the
            university.
          </Typography>
          <TableContainer
            component={Paper}
            className={classes.tableContainer}
            elevation={3}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>
                    Faculty
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner University
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner Course Code
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner Course Title
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner MCs
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS Course Code
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS Course Title
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS MCs
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCourseMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell className={classes.tableCell}>
                      {mapping.faculty}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.partnerUniversity}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.courseCode}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.courseTitle}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.courseUnits}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.nusCourseCode}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.nusCourseTitle}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {mapping.nusCourseUnits}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <TableContainer
            component={Paper}
            className={classes.tableContainer}
            elevation={3}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>
                    Faculty
                  </TableCell>
                  <TableCell
                    className={classes.tableHeaderCell}
                    onClick={handleHeaderClick}
                    style={{ cursor: "pointer" }}
                  >
                    Partner University 🔎
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner Course Code
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner Course Title
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    Partner MCs
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS Course Code
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS Course Title
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    NUS MCs
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell className={classes.tableCell}>
                      {course.faculty}
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      onClick={() =>
                        handleUniversityClick(course.partnerUniversity)
                      }
                    >
                      {course.partnerUniversity}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.courseCode}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.courseTitle}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.courseUnits}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.nusCourseCode}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.nusCourseTitle}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {course.nusCourseUnits}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className={classes.paginationBox}>
            <Button
              variant="contained"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={classes.paginationButton}
            >
              &lt;
            </Button>
            <Typography variant="body1">
              Page {currentPage} of {totalPages}
            </Typography>
            <Button
              variant="contained"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={classes.paginationButton}
            >
              &gt;
            </Button>
          </Box>
        </>
      )}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Enter Partner University</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Partner University"
            type="text"
            fullWidth
            value={universityInput}
            onChange={(e) => setUniversityInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDialogSubmit} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseTable;
